"use client";

import { UserManager } from "oidc-client-ts";
import { type ReactNode, useEffect, useState } from "react";
import { getUserManagerConfig } from "@/config/auth";
import { REALMS } from "@/constants/process";
import { UserManagersContext } from "@/context/user-managers";
import { useLocalizedRouter } from "@/hooks/use-localized-router";
import PeopleRepository from "@/repositories/people/people-repository";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import type { RealmSliceState } from "@/store/realm-slice";
import { syncTokensToMiddleware } from "@/utils/auth-cookies";

export default function UserManagersProvider({ children }: { children: ReactNode }) {
  const [evbasicUserManager, setEvbasicUserManager] = useState<UserManager>();
  const [evcoreUserManager, setEvcoreUserManager] = useState<UserManager>();
  const [silentRefreshInProgress, setSilentRefreshInProgress] = useState(false);

  const { navigateTo } = useLocalizedRouter();
  const intentUrl = usePersistedBoundStore.use.intentUrl();
  const setIntentUrl = usePersistedBoundStore.use.setIntentUrl();

  useEffect(() => {
    setEvbasicUserManager(
      new UserManager(getUserManagerConfig(REALMS.EVBASIC as RealmSliceState["realm"]))
    );
    setEvcoreUserManager(
      new UserManager(getUserManagerConfig(REALMS.EVCORE as RealmSliceState["realm"]))
    );
  }, []);

  useEffect(() => {
    if (!evbasicUserManager || !evcoreUserManager) {
      return;
    }

    // Handler for evbasic - simple navigation to home or intent URL
    function handleEvbasicUserLoaded() {
      // Skip navigation logic if this is a silent refresh
      if (silentRefreshInProgress) {
        return;
      }

      syncTokensToMiddleware();

      // Check if there's an intent URL to redirect to
      if (intentUrl) {
        navigateTo(intentUrl);
        setIntentUrl(undefined); // Clear intent URL after using it
      } else {
        navigateTo("/");
      }
    }

    // Handler for evcore - check profile completion
    async function handleEvcoreUserLoaded() {
      // Skip profile completion logic if this is a silent refresh
      if (silentRefreshInProgress) {
        return;
      }

      try {
        const user = await evcoreUserManager?.getUser();
        const email = user?.profile?.email;

        if (!email) {
          syncTokensToMiddleware();
          navigateTo("/");
          return;
        }

        // Check if user has completed their profile
        const peopleRepository = PeopleRepository.getInstance();
        const personData = await peopleRepository.getPersonByEmail(email);
        const person = personData.data;
        const hasCompleteProfile = person?.completeProfile ?? false;

        if (hasCompleteProfile) {
          // User has completed their profile, proceed to home or intent URL
          syncTokensToMiddleware();

          // Check if there's an intent URL to redirect to
          if (intentUrl) {
            navigateTo(intentUrl);
            setIntentUrl(undefined); // Clear intent URL after using it
            return;
          }
          navigateTo("/");
          return;
        }
        // User has not completed their profile, redirect to normal register
        // Don't redirect to intent URL or clear it if profile is incomplete
        navigateTo("/auth/register/normal");
      } catch {
        // Fallback to home page on error
        syncTokensToMiddleware();
        navigateTo("/");
      }
    }

    // Set up event handlers
    evbasicUserManager?.events.addUserLoaded(handleEvbasicUserLoaded);
    evcoreUserManager?.events.addUserLoaded(handleEvcoreUserLoaded);

    return () => {
      evbasicUserManager?.events.removeUserLoaded(handleEvbasicUserLoaded);
      evcoreUserManager?.events.removeUserLoaded(handleEvcoreUserLoaded);
    };
  }, [
    evbasicUserManager,
    evcoreUserManager,
    navigateTo,
    intentUrl,
    setIntentUrl,
    silentRefreshInProgress,
  ]);

  return (
    <UserManagersContext.Provider
      value={{
        evbasicUserManager,
        evcoreUserManager,
        setSilentRefreshInProgress,
      }}
    >
      {children}
    </UserManagersContext.Provider>
  );
}
